import {DailyAvailability} from '@/components/DailyAvailability';
import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export function Dashboard() {
  const { user, refreshMe } = useAuth();
  const [resendingVerification, setResendingVerification] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [fees, setFees] = useState({});
  const [summary, setSummary] = useState({});
  const [rfqInboxOpen, setRfqInboxOpen] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const list = await api("/me/orgs");
        setOrgs(list.data || []);
      } catch {}
      try {
        const f = await api("/catalog/fees");
        const map = {};
        (f.data || []).forEach((r) => (map[r.key] = r.value_json));
        setFees(map);
      } catch {}
      try {
        const s = await api("/me/inbox-summary");
        setSummary(s || {});
      } catch {}
      try {
        // CP-14.6.4 polish — surface RFQs awaiting the viewer's response.
        // Pulls only `sent` status (the only state where the recipient can act).
        const r = await api("/rfq/inbox?status=sent&limit=200");
        setRfqInboxOpen((r.items || []).length);
      } catch {}
    })();
  }, []);

  const isClient = user?.roles?.includes("client");
  const isLead = user?.roles?.includes("lead_contractor");
  const isHelper = user?.roles?.includes("helper");
  const isContractor = user?.roles?.includes("contractor");
  const isAdmin = user?.roles?.includes("super_admin");

  async function resendVerification() {
    setResendingVerification(true);
    try {
      const result = await api("/auth/resend-verification", { method: "POST" });
      if (result?.already_verified) {
        await refreshMe();
        toast.success("Your email is already verified.");
      } else {
        toast.success("A new verification email has been sent.");
      }
    } catch (e) {
      toast.error(e?.problem?.detail || e?.problem?.title || e.message);
    } finally {
      setResendingVerification(false);
    }
  }

  return (
    <Layout>
      {(isLead || isContractor || isHelper) && <DailyAvailability compact/>}
      {(summary?.client_jobs || summary?.lead_bookings) && (
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          {summary.client_jobs && (
            <Link to="/app/jobs" className="avcp-card p-4 flex items-center justify-between hover:border-[var(--avcp-primary)]"
                  data-testid={TID.dashClientActiveJobs}>
              <div>
                <div className="text-xs text-[var(--avcp-ink-soft)]">Active jobs</div>
                <div className="text-2xl font-bold tracking-tight">{summary.client_jobs.active}</div>
                <div className="text-xs text-[var(--avcp-ink-soft)] mt-1">
                  {summary.client_jobs.published} published · {summary.client_jobs.booking_requested} requested · {summary.client_jobs.accepted} accepted
                </div>
              </div>
              <span className="avcp-pill">view</span>
            </Link>
          )}
          {summary.lead_bookings && (
            <Link to="/app/leads/me/bookings" className="avcp-card p-4 flex items-center justify-between hover:border-[var(--avcp-primary)]"
                  data-testid={TID.dashLeadPending}>
              <div>
                <div className="text-xs text-[var(--avcp-ink-soft)]">Pending booking requests</div>
                <div className="text-2xl font-bold tracking-tight">{summary.lead_bookings.pending}</div>
                <div className="text-xs text-[var(--avcp-ink-soft)] mt-1">
                  {summary.lead_bookings.accepted} accepted · {summary.lead_bookings.declined} declined
                </div>
              </div>
              <span className="avcp-pill">open inbox</span>
            </Link>
          )}
        </div>
      )}

      {/* CP-6.3 — Phase 6 inbox tiles */}
      {(summary?.lead_pending_approvals != null
          || summary?.helper_pending_timesheets != null) && (
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          {summary.lead_pending_approvals != null && (
            <Link to="/app/leads/me/bookings"
                  className="avcp-card p-4 flex items-center justify-between hover:border-[var(--avcp-primary)]"
                  data-testid={TID.dashPendingApprovals}>
              <div>
                <div className="text-xs text-[var(--avcp-ink-soft)]">Helper timesheets awaiting your approval</div>
                <div className="text-2xl font-bold tracking-tight">{summary.lead_pending_approvals}</div>
                <div className="text-xs text-[var(--avcp-ink-soft)] mt-1">
                  Open a booking → Manage Crew → Timesheets to approve.
                </div>
              </div>
              <span className="avcp-pill">review</span>
            </Link>
          )}
          {summary.helper_pending_timesheets != null && (
            <Link to="/app/helpers/me/invitations"
                  className="avcp-card p-4 flex items-center justify-between hover:border-[var(--avcp-primary)]"
                  data-testid={TID.dashPendingTimesheets}>
              <div>
                <div className="text-xs text-[var(--avcp-ink-soft)]">Timesheets to submit</div>
                <div className="text-2xl font-bold tracking-tight">{summary.helper_pending_timesheets}</div>
                <div className="text-xs text-[var(--avcp-ink-soft)] mt-1">
                  Bookings where your hours are pending.
                </div>
              </div>
              <span className="avcp-pill">log hours</span>
            </Link>
          )}
        </div>
      )}

      {!user?.email_verified && (
        <div data-testid={TID.verifyBanner} className="avcp-card p-4 mb-6 flex items-center justify-between">
          <div>
            <span className="avcp-pill warn">Email not verified</span>
            <span className="ml-3 text-sm text-[var(--avcp-ink-soft)]">
              Verify your email to unlock job creation and booking acceptance.
            </span>
          </div>
          <button
            type="button"
            className="avcp-btn ghost"
            disabled={resendingVerification}
            onClick={resendVerification}
          >
            {resendingVerification ? "Sending…" : "Resend verification email"}
          </button>
        </div>
      )}
      {user?.must_rotate_password && (
        <div className="avcp-card p-4 mb-6 flex items-center justify-between border-l-4" style={{ borderLeftColor: "var(--avcp-warning)" }}>
          <div>
            <span className="avcp-pill warn">Password change required</span>
            <span className="ml-3 text-sm text-[var(--avcp-ink-soft)]">
              You are using a temporary password. Please change it now.
            </span>
          </div>
          <Link to="/app/account" className="avcp-btn primary">Change password</Link>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-5">
        <div className="avcp-card p-6 sm:col-span-2">
          <h2 className="text-lg">Welcome, {user?.display_name || user?.email}.</h2>
          <p className="mt-2 text-sm text-[var(--avcp-ink-soft)]">
            Workforce Partners — your dashboard for projects, booking requests, and the AV workforce
            you rely on. Open a section from the nav to get started.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {user?.roles?.map((r) => <span key={r} className="avcp-pill">{r.replace(/_/g, " ")}</span>)}
          </div>
        </div>
        <div className="avcp-card p-6">
          <div className="text-sm text-[var(--avcp-ink-soft)]">Platform fees (per hour)</div>
          <div className="mt-3 grid gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm">Client fee</span>
              <span className="font-mono">
                {fees["fees.client_per_hour"] ? `${fees["fees.client_per_hour"].amount} ${fees["fees.client_per_hour"].currency}` : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Contractor fee</span>
              <span className="font-mono">
                {fees["fees.contractor_per_hour"] ? `${fees["fees.contractor_per_hour"].amount} ${fees["fees.contractor_per_hour"].currency}` : "—"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {rfqInboxOpen > 0 && (
        <div className="mt-6">
          <Link
            to="/app/rfq"
            className="avcp-card p-4 flex items-center justify-between hover:border-[var(--avcp-primary)]"
            data-testid="dashboard-rfq-awaiting-tile"
          >
            <div>
              <div className="text-xs text-[var(--avcp-ink-soft)]">RFQs awaiting your response</div>
              <div className="text-2xl font-bold tracking-tight">{rfqInboxOpen}</div>
              <div className="text-xs text-[var(--avcp-ink-soft)] mt-1">
                Open requests where the other side is waiting on your accept, decline, or message.
              </div>
            </div>
            <span className="avcp-pill">open inbox</span>
          </Link>
        </div>
      )}

      {isClient && (
        <ClientPanel orgs={orgs} reload={async () => setOrgs((await api("/me/orgs")).data || [])} />
      )}
      {(isLead || isContractor || isHelper) && !isClient && (
        <div className="mt-8 avcp-card p-6">
          <h2 className="text-lg">Your work profile</h2>
          <p className="mt-2 text-sm text-[var(--avcp-ink-soft)]">
            Keep your skills, rates and availability up to date. {user?.workforce_kind === "independent" ? "Review Job Requests and Invitations for work you can take on as an individual technician." : "Use Find Crew when you need extra people for a confirmed job."}
          </p>
          <Link to="/app/contractor-profile" className="avcp-btn primary mt-3" data-testid="dashboard-open-profile">
            Open contractor profile
          </Link>
        </div>
      )}
      {isAdmin && (
        <div className="mt-8 avcp-card p-6">
          <h2 className="text-lg">Super Admin</h2>
          <p className="mt-2 text-sm text-[var(--avcp-ink-soft)]">
            Manage users, fees, configuration, and audit log from the Admin console.
          </p>
          <Link to="/app/admin" className="avcp-btn primary mt-3">Open admin console</Link>
        </div>
      )}
    </Layout>
  );
}

function ClientPanel({ orgs, reload }) {
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [currencies, setCurrencies] = useState(["USD", "CAD", "EUR", "GBP"]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await api("/catalog/currencies");
        if (c.data?.length) setCurrencies(c.data);
      } catch {}
    })();
  }, []);

  async function createOrg(e) {
    e.preventDefault(); setBusy(true);
    try {
      await api("/orgs", { method: "POST", body: { name, kind: "client", default_currency: currency } });
      toast.success("Organization created");
      setName("");
      await reload();
    } catch (e) {
      toast.error(e?.problem?.detail || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-8 grid lg:grid-cols-2 gap-5">
      <div className="avcp-card p-6">
        <h2 className="text-lg">Your organizations</h2>
        {orgs.length === 0 ? (
          <p className="mt-2 text-sm text-[var(--avcp-ink-soft)]">No organizations yet. Create one to start booking Lead Technicians on Workforce Partners.</p>
        ) : (
          <ul className="mt-4 grid gap-3">
            {orgs.map((o) => (
              <li key={o.uuid} className="flex items-center justify-between border-b border-[var(--avcp-line)] pb-3">
                <div>
                  <div className="font-medium">{o.name}</div>
                  <div className="text-xs text-[var(--avcp-ink-soft)] font-mono">{o.kind} · {o.default_currency}</div>
                </div>
                <div className="flex gap-1.5">{(o.roles_in_org || []).map((r) => <span key={r} className="avcp-pill ink">{r.replace(/_/g, " ")}</span>)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="avcp-card p-6">
        <h2 className="text-lg">Create a client organization</h2>
        <form onSubmit={createOrg} className="mt-4 grid gap-3">
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Organization name</span>
            <input data-testid={TID.orgName} className="avcp-input" required value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="grid gap-1.5">
            <span className="text-sm font-medium">Default currency</span>
            <select data-testid={TID.orgCurrency} className="avcp-input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <button data-testid={TID.orgSubmit} className="avcp-btn primary" disabled={busy || !name}>
            {busy ? "Creating…" : "Create organization"}
          </button>
        </form>
      </div>
    </div>
  );
}
